import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { persistAuth as persistAuthSession, hasAction } from './utils/auth';
import { prefetchForSession } from './utils/refDataCache';
import { prefetchSearchResults } from './utils/searchCache';
import './pagecss/Login.css';

const API_URL = process.env.REACT_APP_API_URL;

// Views: 'login' | 'forgot' | 'reset' | 'success' | 'setup-otp' | 'setup-password' | 'otp-login'
const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Where a fresh login lands when no returnTo was requested. Role-aware:
  // /dashboard needs action 00054, and a role without it (e.g. ShpRec
  // dispatchers, whose ONLY page is the queue) used to land straight on
  // "No access to this page". Runs after persistAuth, so the just-stored
  // actions claim is readable.
  const defaultLanding = () => {
    if (hasAction('00054', true)) return '/dashboard';   // fallback true: legacy tokens keep old behavior
    if (hasAction('00080', false)) return '/my-shipments';
    return '/home';
  };

  // Pull a sanitized returnTo off the URL — only allow same-app paths so an
  // attacker can't craft a /login?returnTo=https://evil.example link.
  const getReturnTo = () => {
    const params = new URLSearchParams(location.search);
    const raw = params.get('returnTo') || '';
    if (!raw.startsWith('/') || raw.startsWith('//')) return defaultLanding();
    return raw;
  };

  // Land the user back on the page they were trying to reach (or the
  // role-aware default).
  const goToPostLoginTarget = () => navigate(getReturnTo(), { replace: true });

  // Shared state
  const [view, setView] = useState('login');

  // Canvas-zoom the login card so it stays a CONSTANT physical size at every
  // browser-zoom level — matching the rest of the app (whose pages are scaled by
  // NavbarLayout's canvas-zoom; the login page renders OUTSIDE the navbar, so it
  // scales itself here). zoom = vw / canvas: as the browser zoom changes vw
  // inversely, the rendered card stays the same physical size.
  const [cardZoom, setCardZoom] = useState(1);
  useEffect(() => {
    const LOGIN_CANVAS = 1600;   // design canvas — LOWER = bigger card
    const apply = () => {
      const vw = document.documentElement.clientWidth || window.innerWidth;
      const vh = document.documentElement.clientHeight || window.innerHeight;
      const canvas = vw >= vh ? LOGIN_CANVAS : 390;   // narrow/portrait canvas on phones
      setCardZoom(vw / canvas);
    };
    apply();
    const timers = [50, 250, 600].map((ms) => setTimeout(apply, ms));
    window.addEventListener('resize', apply);
    let ro;
    try { ro = new ResizeObserver(apply); ro.observe(document.documentElement); } catch (_) { /* timers + resize cover it */ }
    return () => { window.removeEventListener('resize', apply); timers.forEach(clearTimeout); if (ro) ro.disconnect(); };
  }, []);
  const [message, setMessage] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Login fields
  const [emailid, setEmailid] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // Forgot / Reset fields
  const [forgotEmail, setForgotEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // First-time setup fields
  const [setupOtp, setSetupOtp] = useState('');
  const [setupToken, setSetupToken] = useState(''); // JWT from OTP verification
  const [setupPassword, setSetupPassword] = useState('');
  const [setupConfirmPassword, setSetupConfirmPassword] = useState('');
  const [showSetupPassword, setShowSetupPassword] = useState(false);
  const [showSetupConfirmPassword, setShowSetupConfirmPassword] = useState(false);

  // OTP-login fields (existing user with password choosing to sign in via OTP)
  const [otpLoginCode, setOtpLoginCode] = useState('');

  // Email validation
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  const isValidEmail = (email) => emailRegex.test(email.trim());

  // "Touched" gates the FORMAT error so it can't fire mid-typing. A field is
  // touched once it has been blurred or the form submitted — validate late,
  // revalidate early: the error appears when the user leaves the field, then
  // updates live so it clears the instant they fix it. (Mirrors Formik's
  // validateOnBlur / React Hook Form's mode:'onTouched'.)
  const [emailTouched, setEmailTouched] = useState(false);
  const [forgotEmailTouched, setForgotEmailTouched] = useState(false);

  // Email-exists check (on blur)
  // null = not checked, true = exists, false = not found
  const [emailExists, setEmailExists] = useState(null);
  const [emailChecking, setEmailChecking] = useState(false);
  const [hasPassword, setHasPassword] = useState(null); // true/false/null
  const [forgotEmailExists, setForgotEmailExists] = useState(null);
  const [forgotEmailChecking, setForgotEmailChecking] = useState(false);

  const checkEmailExists = async (email, setExists, setChecking, trackPassword = false) => {
    // Only check if format is valid
    if (!isValidEmail(email)) {
      setExists(null);
      if (trackPassword) setHasPassword(null);
      return;
    }
    setChecking(true);
    try {
      const res = await fetch(
        `${API_URL}/api/Auth/CheckEmail?email=${encodeURIComponent(email.trim())}`
      );
      const data = await res.json();
      setExists(data.exists === true);
      if (trackPassword) {
        setHasPassword(data.hasPassword === true);
      }
    } catch {
      setExists(null); // silently ignore network errors on blur
      if (trackPassword) setHasPassword(null);
    } finally {
      setChecking(false);
    }
  };

  // Stash auth response in sessionStorage — delegates to the central session
  // writer so the Login screen, NavbarLayout logout, and the global 401
  // interceptor all touch the same keys.
  const persistAuth = persistAuthSession;

  // ── LOGIN ─────────────────────────────────────────────────────
  const handleLogin = async (e) => {
    e.preventDefault();
    setMessage('');
    // Arm per-keystroke revalidation only if there is something to revalidate.
    // Submitting an EMPTY box is answered by the banner; arming touched here
    // would flag the user's very first character on the way to a valid address.
    if (emailid) setEmailTouched(true);

    if (!isValidEmail(emailid)) {
      setMessage('Please enter a valid email address (e.g. name@company.com)');
      return;
    }

    if (emailExists === false) {
      setMessage('No account found with this email address.');
      return;
    }

    setIsLoading(true);

    const payload = {
      Email: emailid,
      Password: password
    };

    try {
      const response = await fetch(`${API_URL}/api/Auth/LoginWithPassword`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (!response.ok) {
        setMessage(data.message || `Server error: ${response.status}`);
        return;
      }

      if (data.success) {
        persistAuth(data);
        prefetchForSession();     // warm the searchable-dropdown lists in the background
        prefetchSearchResults();  // …and the list pages' first-page results (self-delayed)
        goToPostLoginTarget();
      } else {
        setMessage(data.message || 'Invalid credentials');
      }
    } catch (error) {
      console.error('Login error:', error);
      if (error.message.includes('Failed to fetch')) {
        setMessage('Unable to connect to server. Please check if the API is running.');
      } else {
        setMessage(`Login failed: ${error.message}`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // ── SETUP: Request OTP for first-time password setup ─────────
  const handleSetupRequestOtp = async () => {
    setMessage('');
    setIsLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/Auth/RequestOtp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ Email: emailid.trim() }),
      });

      const data = await response.json();

      if (data.success) {
        setMessage('');
        setSuccessMsg(data.message || 'A 6-digit verification code has been sent to your email.');
        setView('setup-otp');
      } else {
        setMessage(data.message || 'Failed to send verification code. Please try again.');
      }
    } catch (error) {
      console.error('RequestOtp error:', error);
      if (error.message.includes('Failed to fetch')) {
        setMessage('Unable to connect to server.');
      } else {
        setMessage(`Request failed: ${error.message}`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // ── OTP LOGIN: send code to existing user with a password ────
  const handleLoginRequestOtp = async () => {
    setMessage('');
    setSuccessMsg('');
    // Arm per-keystroke revalidation only if there is something to revalidate.
    // Submitting an EMPTY box is answered by the banner; arming touched here
    // would flag the user's very first character on the way to a valid address.
    if (emailid) setEmailTouched(true);

    if (!isValidEmail(emailid)) {
      setMessage('Please enter a valid email address (e.g. name@company.com)');
      return;
    }
    if (emailExists === false) {
      setMessage('No account found with this email address.');
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/Auth/RequestOtp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ Email: emailid.trim() }),
      });

      const data = await response.json();

      if (data.success) {
        setSuccessMsg(data.message || 'A 6-digit login code has been sent to your email.');
        setOtpLoginCode('');
        setView('otp-login');
      } else {
        setMessage(data.message || 'Failed to send login code. Please try again.');
      }
    } catch (error) {
      console.error('RequestOtp (login) error:', error);
      if (error.message.includes('Failed to fetch')) {
        setMessage('Unable to connect to server.');
      } else {
        setMessage(`Request failed: ${error.message}`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // ── OTP LOGIN: verify code → store JWT → navigate home ───────
  const handleOtpLoginVerify = async (e) => {
    e.preventDefault();
    setMessage('');
    setSuccessMsg('');
    setIsLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/Auth/VerifyOtp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ Email: emailid.trim(), Otp: otpLoginCode }),
      });

      const data = await response.json();

      if (data.success && data.token) {
        persistAuth(data);
        prefetchForSession();     // warm the searchable-dropdown lists in the background
        prefetchSearchResults();  // …and the list pages' first-page results (self-delayed)
        goToPostLoginTarget();
      } else {
        setMessage(data.message || 'Invalid or expired code. Please request a new one.');
      }
    } catch (error) {
      console.error('OTP login verify error:', error);
      if (error.message.includes('Failed to fetch')) {
        setMessage('Unable to connect to server.');
      } else {
        setMessage(`Verification failed: ${error.message}`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // ── SETUP: Verify OTP ────────────────────────────────────────
  const handleSetupVerifyOtp = async (e) => {
    e.preventDefault();
    setMessage('');
    setSuccessMsg('');
    setIsLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/Auth/VerifyOtp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ Email: emailid.trim(), Otp: setupOtp }),
      });

      const data = await response.json();

      if (data.success && data.token) {
        // Store the JWT temporarily — needed for SetPassword
        setSetupToken(data.token);
        setSuccessMsg('Email verified! Now set your password.');
        setMessage('');
        setView('setup-password');
      } else {
        setMessage(data.message || 'Invalid or expired code. Please try again.');
      }
    } catch (error) {
      console.error('VerifyOtp error:', error);
      if (error.message.includes('Failed to fetch')) {
        setMessage('Unable to connect to server.');
      } else {
        setMessage(`Verification failed: ${error.message}`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // ── SETUP: Set Password (first time) ─────────────────────────
  const handleSetupSetPassword = async (e) => {
    e.preventDefault();
    setMessage('');
    setSuccessMsg('');

    if (setupPassword.length < 8) {
      setMessage('Password must be at least 8 characters.');
      return;
    }

    if (setupPassword !== setupConfirmPassword) {
      setMessage('Passwords do not match.');
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/Auth/SetPassword`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${setupToken}`,
        },
        body: JSON.stringify({
          NewPassword: setupPassword,
          ConfirmPassword: setupConfirmPassword,
        }),
      });

      const data = await response.json();

      if (data.success) {
        // Clear setup state
        setSetupOtp('');
        setSetupToken('');
        setSetupPassword('');
        setSetupConfirmPassword('');
        setMessage('');
        setSuccessMsg('Password set successfully! You can now sign in.');
        setView('success');
      } else {
        setMessage(data.message || 'Failed to set password. Please try again.');
      }
    } catch (error) {
      console.error('SetPassword error:', error);
      if (error.message.includes('Failed to fetch')) {
        setMessage('Unable to connect to server.');
      } else {
        setMessage(`Set password failed: ${error.message}`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // ── FORGOT PASSWORD — send OTP ────────────────────────────────
  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setMessage('');
    // Arm per-keystroke revalidation only if there is something to revalidate.
    // Submitting an EMPTY box is answered by the banner; arming touched here
    // would flag the user's very first character on the way to a valid address.
    if (forgotEmail) setForgotEmailTouched(true);

    if (!isValidEmail(forgotEmail)) {
      setMessage('Please enter a valid email address (e.g. name@company.com)');
      return;
    }

    if (forgotEmailExists === false) {
      setMessage('No account found with this email address.');
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/Auth/ForgotPassword`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ Email: forgotEmail }),
      });

      const data = await response.json();

      if (data.success) {
        setMessage('');
        setSuccessMsg(data.message || 'A reset code has been sent to your email.');
        setView('reset');
      } else {
        setMessage(data.message || 'Something went wrong. Please try again.');
      }
    } catch (error) {
      console.error('ForgotPassword error:', error);
      if (error.message.includes('Failed to fetch')) {
        setMessage('Unable to connect to server.');
      } else {
        setMessage(`Request failed: ${error.message}`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // ── RESET PASSWORD — verify OTP + set new password ────────────
  const handleResetPassword = async (e) => {
    e.preventDefault();
    setMessage('');
    setSuccessMsg('');

    if (newPassword !== confirmPassword) {
      setMessage('Passwords do not match.');
      return;
    }

    if (newPassword.length < 8) {
      setMessage('Password must be at least 8 characters.');
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/Auth/ResetPassword`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          Email: forgotEmail,
          Otp: otp,
          NewPassword: newPassword,
          ConfirmPassword: confirmPassword,
        }),
      });

      const data = await response.json();

      if (data.success) {
        // Reset all fields
        setOtp('');
        setNewPassword('');
        setConfirmPassword('');
        setForgotEmail('');
        setMessage('');
        setSuccessMsg(data.message || 'Password reset successfully!');
        setView('success');
      } else {
        setMessage(data.message || 'Reset failed. Please try again.');
      }
    } catch (error) {
      console.error('ResetPassword error:', error);
      if (error.message.includes('Failed to fetch')) {
        setMessage('Unable to connect to server.');
      } else {
        setMessage(`Reset failed: ${error.message}`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // ── Navigate between views ────────────────────────────────────
  const goToForgot = () => {
    setMessage('');
    setSuccessMsg('');
    setForgotEmail(emailid); // pre-fill with login email if entered
    setForgotEmailExists(null);
    setForgotEmailTouched(false);
    setView('forgot');
  };

  const goToLogin = () => {
    setMessage('');
    setSuccessMsg('');
    setOtp('');
    setNewPassword('');
    setConfirmPassword('');
    setSetupOtp('');
    setSetupToken('');
    setSetupPassword('');
    setSetupConfirmPassword('');
    setOtpLoginCode('');
    setEmailExists(null);
    setHasPassword(null);
    setEmailTouched(false);
    setForgotEmailExists(null);
    setForgotEmailTouched(false);
    setView('login');
  };

  // ── Shared UI pieces ──────────────────────────────────────────
  const ErrorMessage = () =>
    message ? (
      <div className="login-error">
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5"/>
          <path d="M8 4.5V8.5M8 10.5V11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
        <span>{message}</span>
      </div>
    ) : null;

  const SuccessMessage = () =>
    successMsg ? (
      <div className="login-success">
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5"/>
          <path d="M5 8.5L7 10.5L11 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
        <span>{successMsg}</span>
      </div>
    ) : null;

  const EmailIcon = () => (
    <svg className="login-input-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="4" width="20" height="16" rx="2"/>
      <path d="M22 7L13.03 12.7a1.94 1.94 0 01-2.06 0L2 7"/>
    </svg>
  );

  const LockIcon = () => (
    <svg className="login-input-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="11" width="18" height="11" rx="2"/>
      <path d="M7 11V7a5 5 0 0110 0v4"/>
    </svg>
  );

  const OtpIcon = () => (
    <svg className="login-input-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="6" width="20" height="12" rx="2"/>
      <path d="M12 12h.01"/>
      <path d="M17 12h.01"/>
      <path d="M7 12h.01"/>
    </svg>
  );

  const EyeButton = ({ show, onToggle }) => (
    <button
      type="button"
      className="login-toggle-password"
      onClick={onToggle}
      tabIndex={-1}
      aria-label={show ? 'Hide password' : 'Show password'}
    >
      {show ? (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94"/>
          <path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19"/>
          <line x1="1" y1="1" x2="23" y2="23"/>
        </svg>
      ) : (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
          <circle cx="12" cy="12" r="3"/>
        </svg>
      )}
    </button>
  );

  // ═════════════════════════════════════════════════════════════
  // VIEW: LOGIN
  // ═════════════════════════════════════════════════════════════
  const renderLogin = () => {
    const showSetupFlow = emailExists === true && hasPassword === false;

    return (
      <>
        <h2 className="login-title">Welcome Back</h2>
        <p className="login-subtitle">Sign in to your account</p>

        <ErrorMessage />

        <form onSubmit={showSetupFlow ? (e) => { e.preventDefault(); handleSetupRequestOtp(); } : handleLogin} className="login-form">
          <div className="login-field">
            <label htmlFor="emailid" className="login-label">Email Address</label>
            <div className="login-input-wrapper">
              <EmailIcon />
              <input
                type="email"
                id="emailid"
                value={emailid}
                onChange={(e) => {
                  setEmailid(e.target.value);
                  setEmailExists(null);
                  setHasPassword(null);
                  // The banner is set by a PREVIOUS action (a failed Sign In, or
                  // "send me a code" with an empty box). Once the user starts
                  // fixing the input it is stale — drop it.
                  if (message) setMessage('');
                }}
                onBlur={() => { setEmailTouched(true); checkEmailExists(emailid, setEmailExists, setEmailChecking, true); }}
                placeholder="you@example.com"
                disabled={isLoading}
                required
                className={`login-input ${emailTouched && emailid && !isValidEmail(emailid) ? 'login-input-invalid' : ''} ${emailExists === false ? 'login-input-invalid' : ''}`}
                autoComplete="email"
              />
              {emailChecking && <span className="login-input-checking"></span>}
            </div>
            {emailTouched && emailid && !isValidEmail(emailid) && (
              <span className="login-field-hint login-field-hint-error">Enter a valid email (e.g. name@company.com)</span>
            )}
            {emailExists === false && isValidEmail(emailid) && (
              <span className="login-field-hint login-field-hint-error">No account found with this email</span>
            )}
          </div>

          {/* Show password field only if user HAS a password */}
          {!showSetupFlow && (
            <div className="login-field">
              <div className="login-label-row">
                <label htmlFor="password" className="login-label">Password</label>
                <button
                  type="button"
                  className="login-forgot-link"
                  onClick={goToForgot}
                >
                  Forgot Password?
                </button>
              </div>
              <div className="login-input-wrapper">
                <LockIcon />
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  disabled={isLoading}
                  required
                  className="login-input"
                  autoComplete="current-password"
                />
                <EyeButton show={showPassword} onToggle={() => setShowPassword(!showPassword)} />
              </div>
            </div>
          )}

          {/* First-time setup notice */}
          {showSetupFlow && (
            <div className="login-setup-notice">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#0052A5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/>
                <path d="M12 16v-4M12 8h.01"/>
              </svg>
              <div>
                <strong>First time here?</strong>
                <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#555' }}>
                  We'll send a verification code to your email to set up your password.
                </p>
              </div>
            </div>
          )}

          <button
            type="submit"
            disabled={isLoading}
            className={`login-btn ${isLoading ? 'login-btn-loading' : ''} ${showSetupFlow ? 'login-btn-setup' : ''}`}
          >
            {isLoading ? (
              <>
                <span className="login-spinner"></span>
                {showSetupFlow ? 'Sending code...' : 'Signing in...'}
              </>
            ) : (
              showSetupFlow ? 'Set Up Your Account' : 'Sign In'
            )}
          </button>

          {/* OTP-login alternative — only shown when password field is visible */}
          {!showSetupFlow && (
            <button
              type="button"
              className="login-back-link"
              onClick={handleLoginRequestOtp}
              disabled={isLoading}
            >
              Sign in with a verification code instead
            </button>
          )}
        </form>
      </>
    );
  };

  // ═════════════════════════════════════════════════════════════
  // VIEW: SETUP OTP (first-time — enter verification code)
  // ═════════════════════════════════════════════════════════════
  const renderSetupOtp = () => (
    <>
      <div className="login-view-icon">
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#0052A5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="2" y="4" width="20" height="16" rx="2"/>
          <path d="M22 7L13.03 12.7a1.94 1.94 0 01-2.06 0L2 7"/>
        </svg>
      </div>
      <h2 className="login-title">Verify Your Email</h2>
      <p className="login-subtitle">
        Enter the 6-digit code sent to <strong>{emailid}</strong>
      </p>

      <SuccessMessage />
      <ErrorMessage />

      <form onSubmit={handleSetupVerifyOtp} className="login-form">
        <div className="login-field">
          <label htmlFor="setup-otp" className="login-label">6-Digit Code</label>
          <div className="login-input-wrapper">
            <OtpIcon />
            <input
              type="text"
              id="setup-otp"
              value={setupOtp}
              onChange={(e) => {
                const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                setSetupOtp(val);
              }}
              placeholder="Enter 6-digit code"
              disabled={isLoading}
              required
              maxLength={6}
              className="login-input login-input-otp"
              autoComplete="one-time-code"
              inputMode="numeric"
              autoFocus
            />
          </div>
          <span className="login-field-hint">Check your inbox and spam folder</span>
        </div>

        <button
          type="submit"
          disabled={isLoading || setupOtp.length !== 6}
          className={`login-btn ${isLoading ? 'login-btn-loading' : ''}`}
        >
          {isLoading ? (
            <>
              <span className="login-spinner"></span>
              Verifying...
            </>
          ) : (
            'Verify Code'
          )}
        </button>
      </form>

      <button
        className="login-back-link"
        onClick={() => {
          setMessage('');
          setSuccessMsg('');
          setSetupOtp('');
          handleSetupRequestOtp(); // Resend OTP
        }}
        type="button"
        disabled={isLoading}
      >
        Resend Code
      </button>

      <button className="login-back-link" onClick={goToLogin} type="button">
        &larr; Back to Sign In
      </button>
    </>
  );

  // ═════════════════════════════════════════════════════════════
  // VIEW: OTP LOGIN (existing user with password — sign in via code)
  // ═════════════════════════════════════════════════════════════
  const renderOtpLogin = () => (
    <>
      <div className="login-view-icon">
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#0052A5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="2" y="6" width="20" height="12" rx="2"/>
          <path d="M7 12h.01"/>
          <path d="M12 12h.01"/>
          <path d="M17 12h.01"/>
        </svg>
      </div>
      <h2 className="login-title">Sign in with a Code</h2>
      <p className="login-subtitle">
        Enter the 6-digit code sent to <strong>{emailid}</strong>
      </p>

      <SuccessMessage />
      <ErrorMessage />

      <form onSubmit={handleOtpLoginVerify} className="login-form">
        <div className="login-field">
          <label htmlFor="otp-login-code" className="login-label">6-Digit Code</label>
          <div className="login-input-wrapper">
            <OtpIcon />
            <input
              type="text"
              id="otp-login-code"
              value={otpLoginCode}
              onChange={(e) => {
                const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                setOtpLoginCode(val);
              }}
              placeholder="Enter 6-digit code"
              disabled={isLoading}
              required
              maxLength={6}
              className="login-input login-input-otp"
              autoComplete="one-time-code"
              inputMode="numeric"
              autoFocus
            />
          </div>
          <span className="login-field-hint">Check your inbox and spam folder</span>
        </div>

        <button
          type="submit"
          disabled={isLoading || otpLoginCode.length !== 6}
          className={`login-btn ${isLoading ? 'login-btn-loading' : ''}`}
        >
          {isLoading ? (
            <>
              <span className="login-spinner"></span>
              Signing in...
            </>
          ) : (
            'Sign In'
          )}
        </button>
      </form>

      <button
        className="login-back-link"
        onClick={() => {
          setMessage('');
          setSuccessMsg('');
          setOtpLoginCode('');
          handleLoginRequestOtp();
        }}
        type="button"
        disabled={isLoading}
      >
        Resend Code
      </button>

      <button className="login-back-link" onClick={goToLogin} type="button">
        &larr; Back to Sign In
      </button>
    </>
  );

  // ═════════════════════════════════════════════════════════════
  // VIEW: SETUP PASSWORD (first-time — set new password)
  // ═════════════════════════════════════════════════════════════
  const renderSetupPassword = () => (
    <>
      <div className="login-view-icon">
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#0052A5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="11" width="18" height="11" rx="2"/>
          <path d="M7 11V7a5 5 0 0110 0v4"/>
        </svg>
      </div>
      <h2 className="login-title">Create Your Password</h2>
      <p className="login-subtitle">
        Set a password for <strong>{emailid}</strong> to complete your account setup.
      </p>

      <SuccessMessage />
      <ErrorMessage />

      <form onSubmit={handleSetupSetPassword} className="login-form">
        {/* New Password */}
        <div className="login-field">
          <label htmlFor="setup-password" className="login-label">Password</label>
          <div className="login-input-wrapper">
            <LockIcon />
            <input
              type={showSetupPassword ? 'text' : 'password'}
              id="setup-password"
              value={setupPassword}
              onChange={(e) => setSetupPassword(e.target.value)}
              placeholder="Min. 8 characters"
              disabled={isLoading}
              required
              minLength={8}
              className="login-input"
              autoComplete="new-password"
              autoFocus
            />
            <EyeButton show={showSetupPassword} onToggle={() => setShowSetupPassword(!showSetupPassword)} />
          </div>
        </div>

        {/* Confirm Password */}
        <div className="login-field">
          <label htmlFor="setup-confirm-password" className="login-label">Confirm Password</label>
          <div className="login-input-wrapper">
            <LockIcon />
            <input
              type={showSetupConfirmPassword ? 'text' : 'password'}
              id="setup-confirm-password"
              value={setupConfirmPassword}
              onChange={(e) => setSetupConfirmPassword(e.target.value)}
              placeholder="Re-enter your password"
              disabled={isLoading}
              required
              minLength={8}
              className="login-input"
              autoComplete="new-password"
            />
            <EyeButton show={showSetupConfirmPassword} onToggle={() => setShowSetupConfirmPassword(!showSetupConfirmPassword)} />
          </div>
          {setupPassword && setupConfirmPassword && setupPassword !== setupConfirmPassword && (
            <span className="login-field-hint login-field-hint-error">Passwords do not match</span>
          )}
        </div>

        <button
          type="submit"
          disabled={isLoading || setupPassword.length < 8}
          className={`login-btn ${isLoading ? 'login-btn-loading' : ''}`}
        >
          {isLoading ? (
            <>
              <span className="login-spinner"></span>
              Setting password...
            </>
          ) : (
            'Set Password & Sign In'
          )}
        </button>
      </form>

      <button className="login-back-link" onClick={goToLogin} type="button">
        &larr; Back to Sign In
      </button>
    </>
  );

  // ═════════════════════════════════════════════════════════════
  // VIEW: FORGOT PASSWORD
  // ═════════════════════════════════════════════════════════════
  const renderForgot = () => (
    <>
      <div className="login-view-icon">
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#0052A5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="11" width="18" height="11" rx="2"/>
          <path d="M7 11V7a5 5 0 0110 0v4"/>
        </svg>
      </div>
      <h2 className="login-title">Forgot Password?</h2>
      <p className="login-subtitle">
        Enter your email and we'll send you a 6-digit code to reset your password.
      </p>

      <ErrorMessage />

      {/* noValidate (UI-AUTH-043): handleForgotPassword checks the address itself and shows
          the app's red banner; without it the browser's own bubble fires first. */}
      <form onSubmit={handleForgotPassword} className="login-form" noValidate>
        <div className="login-field">
          <label htmlFor="forgot-email" className="login-label">Email Address</label>
          <div className="login-input-wrapper">
            <EmailIcon />
            <input
              type="email"
              id="forgot-email"
              value={forgotEmail}
              onChange={(e) => {
                setForgotEmail(e.target.value);
                setForgotEmailExists(null);
                if (message) setMessage('');
              }}
              onBlur={() => { setForgotEmailTouched(true); checkEmailExists(forgotEmail, setForgotEmailExists, setForgotEmailChecking); }}
              placeholder="you@example.com"
              disabled={isLoading}
              required
              className={`login-input ${forgotEmailTouched && forgotEmail && !isValidEmail(forgotEmail) ? 'login-input-invalid' : ''} ${forgotEmailExists === false ? 'login-input-invalid' : ''}`}
              autoComplete="email"
            />
            {forgotEmailChecking && <span className="login-input-checking"></span>}
          </div>
          {forgotEmailTouched && forgotEmail && !isValidEmail(forgotEmail) && (
            <span className="login-field-hint login-field-hint-error">Enter a valid email (e.g. name@company.com)</span>
          )}
          {forgotEmailExists === false && isValidEmail(forgotEmail) && (
            <span className="login-field-hint login-field-hint-error">No account found with this email</span>
          )}
        </div>

        <button
          type="submit"
          disabled={isLoading}
          className={`login-btn ${isLoading ? 'login-btn-loading' : ''}`}
        >
          {isLoading ? (
            <>
              <span className="login-spinner"></span>
              Sending code...
            </>
          ) : (
            'Send Reset Code'
          )}
        </button>
      </form>

      <button className="login-back-link" onClick={goToLogin} type="button">
        &larr; Back to Sign In
      </button>
    </>
  );

  // ═════════════════════════════════════════════════════════════
  // VIEW: RESET PASSWORD (OTP + new password)
  // ═════════════════════════════════════════════════════════════
  const renderReset = () => (
    <>
      <div className="login-view-icon">
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#0052A5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
        </svg>
      </div>
      <h2 className="login-title">Reset Password</h2>
      <p className="login-subtitle">
        Enter the code sent to <strong>{forgotEmail}</strong> and choose a new password.
      </p>

      <SuccessMessage />
      <ErrorMessage />

      <form onSubmit={handleResetPassword} className="login-form">
        {/* OTP */}
        <div className="login-field">
          <label htmlFor="otp" className="login-label">6-Digit Code</label>
          <div className="login-input-wrapper">
            <OtpIcon />
            <input
              type="text"
              id="otp"
              value={otp}
              onChange={(e) => {
                const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                setOtp(val);
              }}
              placeholder="Enter 6-digit code"
              disabled={isLoading}
              required
              maxLength={6}
              className="login-input login-input-otp"
              autoComplete="one-time-code"
              inputMode="numeric"
            />
          </div>
        </div>

        {/* New Password */}
        <div className="login-field">
          <label htmlFor="new-password" className="login-label">New Password</label>
          <div className="login-input-wrapper">
            <LockIcon />
            <input
              type={showNewPassword ? 'text' : 'password'}
              id="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Min. 8 characters"
              disabled={isLoading}
              required
              minLength={8}
              className="login-input"
              autoComplete="new-password"
            />
            <EyeButton show={showNewPassword} onToggle={() => setShowNewPassword(!showNewPassword)} />
          </div>
        </div>

        {/* Confirm Password */}
        <div className="login-field">
          <label htmlFor="confirm-password" className="login-label">Confirm Password</label>
          <div className="login-input-wrapper">
            <LockIcon />
            <input
              type={showConfirmPassword ? 'text' : 'password'}
              id="confirm-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Re-enter your password"
              disabled={isLoading}
              required
              minLength={8}
              className="login-input"
              autoComplete="new-password"
            />
            <EyeButton show={showConfirmPassword} onToggle={() => setShowConfirmPassword(!showConfirmPassword)} />
          </div>
        </div>

        <button
          type="submit"
          disabled={isLoading || otp.length !== 6}
          className={`login-btn ${isLoading ? 'login-btn-loading' : ''}`}
        >
          {isLoading ? (
            <>
              <span className="login-spinner"></span>
              Resetting...
            </>
          ) : (
            'Reset Password'
          )}
        </button>
      </form>

      <button className="login-back-link" onClick={goToLogin} type="button">
        &larr; Back to Sign In
      </button>
    </>
  );

  // ═════════════════════════════════════════════════════════════
  // VIEW: SUCCESS
  // ═════════════════════════════════════════════════════════════
  const renderSuccess = () => (
    <>
      <div className="login-success-icon">
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"/>
          <path d="M8 12l3 3 5-5"/>
        </svg>
      </div>
      <h2 className="login-title">You're All Set!</h2>
      <p className="login-subtitle">
        {successMsg || 'Your password has been set successfully. You can now sign in.'}
      </p>

      <button
        type="button"
        className="login-btn"
        onClick={goToLogin}
      >
        Sign In
      </button>
    </>
  );

  // ═════════════════════════════════════════════════════════════
  // RENDER
  // ═════════════════════════════════════════════════════════════
  return (
    <div className="login-page">
      <div className="login-bg-shape login-bg-shape-1"></div>
      <div className="login-bg-shape login-bg-shape-2"></div>

      <div className="login-card" key={view} style={{ zoom: cardZoom }}>
        {/* Logo — always visible */}
        <div className="login-logo-section">
          <img
            src="/Images/SideLogoSlogan.webp"
            alt="Shukla Medical"
            className="login-logo"
          />
        </div>
        <div className="login-divider"></div>

        {/* Dynamic view */}
        {view === 'login' && renderLogin()}
        {view === 'forgot' && renderForgot()}
        {view === 'reset' && renderReset()}
        {view === 'setup-otp' && renderSetupOtp()}
        {view === 'setup-password' && renderSetupPassword()}
        {view === 'otp-login' && renderOtpLogin()}
        {view === 'success' && renderSuccess()}

        {/* Back to home — only on login view */}
        {view === 'login' && (
          <button
            className="login-back-link"
            onClick={() => navigate('/home')}
            type="button"
          >
            &larr; Back to Home
          </button>
        )}
      </div>
    </div>
  );
};

export default Login;
